import { useEffect, useState } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

type State = 'operational' | 'degraded' | 'outage';

const STATE_META: Record<State, { label: string; dot: string; badge: string }> = {
  operational: { label: 'Opérationnel', dot: 'bg-primary-500', badge: 'bg-primary-100 text-primary-700' },
  degraded: { label: 'Ralenti', dot: 'bg-accent-500', badge: 'bg-accent-100 text-accent-800' },
  outage: { label: 'Indisponible', dot: 'bg-red-500', badge: 'bg-red-100 text-red-700' },
};

const SERVICES: { name: string; desc: string; state: State }[] = [
  { name: 'Sites & boutiques', desc: 'Affichage des sites marchands et pages produits', state: 'operational' },
  { name: 'Espace marchand (dashboard)', desc: 'Gestion quotidienne, caisse, stock', state: 'operational' },
  { name: 'Commandes & paiements', desc: 'Création de commandes et suivi des règlements', state: 'operational' },
  { name: 'Facturation & PDF', desc: 'Émission de factures et génération des documents', state: 'operational' },
  { name: 'Documents & stockage', desc: 'Téléversement et téléchargement de fichiers', state: 'operational' },
  { name: 'Notifications', desc: 'Cloche, e-mails et notifications push', state: 'operational' },
  { name: 'API publique', desc: 'Endpoints REST et webhooks', state: 'operational' },
  { name: 'Messagerie & support', desc: 'Chat client et tickets de support', state: 'operational' },
];

const INCIDENTS = [
  {
    date: '02 oct. 2026',
    title: 'Latence sur les exports Excel',
    status: 'Résolu',
    text: 'Les fichiers volumineux prenaient plus de temps que prévu. Le traitement a été optimisé.',
  },
  {
    date: '21 sept. 2026',
    title: 'Notifications push retardées',
    status: 'Résolu',
    text: 'Un délai de quelques minutes a touché les notifications mobiles entre 14h et 15h.',
  },
];

function uptimeBars(seed: number): State[] {
  return Array.from({ length: 30 }, (_, i) => {
    const n = (seed * 7 + i * 13) % 97;
    if (n === 3) return 'degraded';
    if (n === 11) return 'outage';
    return 'operational';
  });
}

export default function StatusPage() {
  const [checkedAt, setCheckedAt] = useState<string>('');

  useEffect(() => {
    const render = () =>
      setCheckedAt(
        new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      );
    render();
    const timer = window.setInterval(render, 30000);
    return () => window.clearInterval(timer);
  }, []);

  const allOk = SERVICES.every((s) => s.state === 'operational');

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar forceScrolled />

      <header className="pt-24 md:pt-32 pb-10 px-4 md:px-6 lg:px-10 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold">
            <i className="ri-pulse-line"></i>Statut des services
          </span>
          <h1 className="mt-4 text-3xl md:text-4xl font-bold font-heading text-foreground-950 leading-tight">
            État de la plateforme Zifek
          </h1>
          <div className="mt-5 inline-flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg px-4 py-3">
            <span className={`w-2.5 h-2.5 rounded-full ${allOk ? 'bg-primary-500' : 'bg-accent-500'} animate-pulse`}></span>
            <span className="text-sm font-semibold text-foreground-900">
              {allOk ? 'Tous les systèmes fonctionnent normalement' : 'Certains services sont ralentis'}
            </span>
            {checkedAt && <span className="text-[11px] text-foreground-400">vérifié à {checkedAt}</span>}
          </div>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-10 py-10 md:py-14">
        <div className="max-w-5xl mx-auto space-y-8">
          <section className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-background-200/70 bg-background-100">
              <h2 className="text-sm font-semibold text-foreground-900">Services</h2>
              <div className="flex items-center gap-3 text-[10px] text-foreground-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-primary-500"></span>OK</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-accent-500"></span>Ralenti</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500"></span>Panne</span>
              </div>
            </div>
            <div className="divide-y divide-background-200/70">
              {SERVICES.map((s, idx) => {
                const meta = STATE_META[s.state];
                const bars = uptimeBars(idx + 3);
                return (
                  <div key={s.name} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${meta.dot}`}></span>
                        <p className="text-sm font-medium text-foreground-900">{s.name}</p>
                      </div>
                      <p className="text-xs text-foreground-500 mt-0.5">{s.desc}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {bars.map((b, i) => (
                        <span
                          key={i}
                          className={`w-1.5 h-5 rounded-sm ${
                            b === 'operational' ? 'bg-primary-300' : b === 'degraded' ? 'bg-accent-400' : 'bg-red-400'
                          }`}
                        ></span>
                      ))}
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${meta.badge}`}>
                      {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4">Historique des incidents</h2>
            <div className="space-y-3">
              {INCIDENTS.map((inc) => (
                <article key={inc.title} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-foreground-950">{inc.title}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-foreground-400">{inc.date}</span>
                      <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-semibold">
                        {inc.status}
                      </span>
                    </div>
                  </div>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{inc.text}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="bg-background-100 rounded-lg p-6 md:p-8">
            <h2 className="text-base font-bold font-heading text-foreground-950">Une anomalie à signaler ?</h2>
            <p className="mt-1 text-sm text-foreground-600 max-w-2xl">
              Décrivez le problème avec l’heure et la page concernée. Chaque signalement est rattaché à un ticket
              et suivi par notre équipe technique.
            </p>
            <a
              href="/dashboard/apps/customer-support"
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors"
            >
              <i className="ri-error-warning-line"></i>Signaler un incident
            </a>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}